import styles from "./index.module.css";
import { useState, useEffect } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import Button from "shared/Button";

/**
 * Displays a box with a value for the user to copy. If auto copy is set to true the value is automatically copied to the users clipboard. valueClassName is optional to add style to the value.
 */

export default function CopyBox({ label, value, autoCopy, valueClassName }) {
  const [showCopiedNotice, setShowCopiedNotice] = useState(false);

  /**
   * Displays notice once value is copied.
   */
  useEffect(() => {
    if (showCopiedNotice) {
      const timeout = setTimeout(() => {
        setShowCopiedNotice(false);
      }, 2000);

      return () => clearTimeout(timeout); // cleanup on unmount
    }
  }, [showCopiedNotice]);

  const handleCopy = () => {
    if (document.hasFocus()) {
      navigator.clipboard
        .writeText(value)
        .then(() => setShowCopiedNotice(true));
    }
  };

  useEffect(() => {
    if (autoCopy && document.hasFocus()) {
      handleCopy();
    }
  }, [autoCopy, value]);

  return (
    <div className={styles.copyBox}>
      <span className={styles.copyLabel}>{label}</span>
      <div className={styles.alignEnd}>
        <input
          type="text"
          value={value || ""}
          disabled
          className={classNames(styles.copyValue, valueClassName)}
        />

        <Button size="small" onClick={handleCopy}>
          Copy
        </Button>
      </div>

      <span
        className={classNames(styles.copiedNotice, {
          [styles.active]: showCopiedNotice,
        })}
      >
        Copied to clipboard
      </span>
    </div>
  );
}

CopyBox.propTypes = {
  /**
   * Label for value
   */
  label: PropTypes.string,
  /**
   * value to be copied.
   */
  value: PropTypes.string,
  /**
   * If set to true the value is automatically copied
   */
  autoCopy: PropTypes.bool,
  /**
   * Adds style to the value.
   */
  valueClassName: PropTypes.string,
};
